import isEqual from 'lodash/isEqual';
import React, { useMemo } from 'react';
import { Editor, Transforms } from 'slate';
import { useSlate, ReactEditor } from 'slate-react';

import { VariantPicker } from '@candu-ai/craft/ui';
import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';
import { VariantNode } from '@candu-ai/styleguide/types';
import { SlateNode } from 'craft/types';

interface VariantItem extends VariantNode {
  value: string;
  tagName?: string;
}

export const VariantSelector = () => {
  const { data } = useDefaultVariants();
  const { variants: styleguideVariants } = data || {};

  const rteEditor = useSlate() as ReactEditor;

  const variants: VariantItem[] = useMemo(
    () =>
      Object.entries(styleguideVariants?.Typography || {})
        .map(
          ([key, value]) =>
            ({
              value: key,
              name: value.name,
              isHidden: value.isHidden,
              conditions: { variant: value.tagName?.toUpperCase() ?? 'P', variation: key },
            } as VariantItem),
        )
        .filter((v) => !v.isHidden),
    [styleguideVariants],
  );

  if (!rteEditor.selection) {
    return null;
  }

  const [match] = Array.from(
    Editor.nodes(rteEditor, {
      at: Editor.unhangRange(rteEditor, rteEditor.selection),
      match: (n: SlateNode) => n?.data?.variant,
    }),
  );
  const { variant, variation, ...nodeData } = (match?.[0] as SlateNode)?.data || {};

  const onChange = (name: string) => {
    const newVariant = variants.find((v) => v.value === name);

    if (!newVariant) {
      return;
    }
    Transforms.setNodes(
      rteEditor,
      {
        data: {
          ...nodeData,
          variant: newVariant.conditions.variant,
          variation: newVariant.conditions.variation,
        },
      },
      {
        at: rteEditor.selection ? Editor.unhangRange(rteEditor, rteEditor.selection) : [],
        match: (n: SlateNode) => n?.data?.variant,
      },
    );
  };

  const currentVariant = variants.find((v) => isEqual(v.value, variation || variant));

  return (
    <VariantPicker
      mode="Dark"
      selectedVariant={variants.find((v) => v.value === currentVariant?.value)?.name}
      onSelectVariant={onChange}
      items={variants}
      tooltipContent="Select Component Variant"
    />
  );
};
